import { useCallback, useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { StateBlock } from "../components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;
const quickFilters = [["tutte", "Tutte"], ["mie", "Le mie"], ["non_assegnate", "Non assegnate"], ["oggi", "Da fare oggi"], ["scadute", "Scadute"], ["senza_attivita", "Senza prossima attività"]];

function request(path, headers, method = "GET", body) {
  return fetch(`${API}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined })
    .then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail?.message || data.detail || "Operazione non riuscita");
      return data;
    });
}

export default function PartnershipModule() {
  const headers = { Authorization: `Bearer ${localStorage.getItem("super_admin_token")}`, "Content-Type": "application/json" };
  const initial = new URLSearchParams(window.location.search);
  const [view, setView] = useState(initial.get("pVista") || "elenco");
  const [filter, setFilter] = useState(initial.get("pFiltro") || "tutte");
  const [selected, setSelected] = useState(initial.get("partnership") || "");
  const [items, setItems] = useState([]);
  const [fasi, setFasi] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request(`/api/admin/cdrpt/partnership/?vista=${filter}`, headers);
      setItems(data.data || []); setFasi(data.fasi || {}); setError("");
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { const q = new URLSearchParams(window.location.search); q.set("pVista", view); q.set("pFiltro", filter); selected ? q.set("partnership", selected) : q.delete("partnership"); window.history.replaceState({}, "", `${window.location.pathname}?${q}`); }, [view, filter, selected]);
  useEffect(() => { load(); }, [load]);
  if (selected) return <PartnershipDetail id={selected} headers={headers} onClose={() => { setSelected(""); load(); }} />;
  if (loading) return <StateBlock state="loading" title="Caricamento partnership strutture…" />;
  if (error) return <StateBlock state="error" title={error} onRetry={load} />;
  return <section className="space-y-4">
    <header className="flex flex-wrap items-center gap-2"><h1 className="mr-auto text-xl font-bold text-admin-text">Partnership strutture</h1><Button size="sm" onClick={() => setCreateOpen(true)}>Nuova partnership</Button><Button size="sm" variant={view === "elenco" ? "default" : "outline"} onClick={() => setView("elenco")}>Elenco</Button><Button size="sm" variant={view === "pipeline" ? "default" : "outline"} onClick={() => setView("pipeline")}>Pipeline</Button></header>
    <CreatePartnershipDialog open={createOpen} headers={headers} onClose={() => setCreateOpen(false)} onCreated={(id) => { setCreateOpen(false); setSelected(id); load(); }} />
    <div className="flex flex-wrap gap-2">{quickFilters.map(([id, label]) => <Button key={id} size="sm" variant={filter === id ? "secondary" : "ghost"} onClick={() => setFilter(id)}>{label}</Button>)}<Button className="ml-auto" size="sm" variant="outline" onClick={load}>Aggiorna</Button></div>
    {view === "elenco" ? <PartnershipList items={items} fasi={fasi} onOpen={setSelected} /> : <PartnershipBoard items={items} fasi={fasi} onOpen={setSelected} />}
  </section>;
}

function CreatePartnershipDialog({ open, headers, onClose, onCreated }) {
  const empty = { struttura: null, query: "", responsabile_email: "", prossima_azione: "", scadenza_at: "" };
  const [form, setForm] = useState(empty);
  const [structures, setStructures] = useState([]);
  const [operators, setOperators] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(empty);
    setStructures([]);
    setError("");
    request("/api/admin/leads/pipeline/operatori", headers).then((result) => setOperators(result.data || [])).catch(() => setOperators([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || form.struttura || form.query.trim().length < 2) {
      setStructures([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ attivo: "all", q: form.query.trim() });
        const result = await request(`/api/admin/strutture?${params}`, headers);
        setStructures((result.strutture || []).slice(0, 8));
      } catch (e) {
        setError(e.message);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [form.query, form.struttura, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.struttura || !form.responsabile_email || !form.prossima_azione.trim() || !form.scadenza_at) {
      setError("Struttura, responsabile, prossima attività e scadenza sono obbligatori.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const owner = operators.find((item) => item.email === form.responsabile_email);
      const result = await request("/api/admin/cdrpt/partnership/", headers, "POST", {
        struttura_id: form.struttura.id,
        responsabile_email: form.responsabile_email,
        responsabile_nome: owner?.nome || form.responsabile_email,
        prossima_azione: form.prossima_azione.trim(),
        scadenza_at: new Date(form.scadenza_at).toISOString(),
      });
      onCreated(result.partnership.id);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuova partnership</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>Struttura</Label>
            {form.struttura ? (
              <div className="mt-1 flex items-center justify-between rounded-md border border-admin-border p-2 text-sm">
                <span>{form.struttura.name}{form.struttura.comune ? ` · ${form.struttura.comune}` : ""}</span>
                <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, struttura: null, query: "" })}>Cambia</Button>
              </div>
            ) : (
              <>
                <Input className="mt-1" value={form.query} placeholder="Cerca per nome o comune" onChange={(event) => setForm({ ...form, query: event.target.value })} />
                <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-admin-border">
                  {searching ? <p className="p-2 text-sm text-admin-muted">Ricerca in corso…</p> : null}
                  {!searching && form.query.trim().length >= 2 && !structures.length ? <p className="p-2 text-sm text-admin-muted">Nessuna struttura trovata.</p> : null}
                  {structures.map((item) => (
                    <button type="button" key={item.id} className="block w-full border-b border-admin-border px-2 py-1.5 text-left text-sm last:border-0 hover:bg-slate-50" onClick={() => setForm({ ...form, struttura: item, query: "" })}>
                      {item.name}{item.comune ? ` · ${item.comune}` : ""}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <div>
            <Label>Responsabile</Label>
            <select className="mt-1 w-full rounded-md border border-admin-border p-2 text-sm" value={form.responsabile_email} onChange={(event) => setForm({ ...form, responsabile_email: event.target.value })}>
              <option value="">Seleziona responsabile</option>
              {operators.map((item) => <option key={item.email} value={item.email}>{item.nome || item.email}</option>)}
            </select>
          </div>
          <div>
            <Label>Prossima attività</Label>
            <Textarea className="mt-1" value={form.prossima_azione} placeholder="Primo contatto da effettuare" onChange={(event) => setForm({ ...form, prossima_azione: event.target.value })} />
          </div>
          <div>
            <Label>Scadenza</Label>
            <Input className="mt-1" type="datetime-local" value={form.scadenza_at} onChange={(event) => setForm({ ...form, scadenza_at: event.target.value })} />
          </div>
          {error ? <p className="rounded-md bg-rose-50 p-2 text-sm text-rose-800">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={saving} onClick={onClose}>Annulla</Button>
          <Button type="button" disabled={saving} onClick={submit}>{saving ? "Creazione…" : "Crea partnership"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartnershipList({ items, fasi, onOpen }) {
  if (!items.length) return <StateBlock state="empty" title="Nessuna opportunità partnership trovata." />;
  return <div className="overflow-x-auto rounded-xl border border-admin-border"><table className="admin-table-sticky-actions w-full"><thead><tr><th>Struttura</th><th>Referente</th><th>Fase</th><th>Responsabile</th><th>Prossima attività</th></tr></thead><tbody>{items.map((x) => <tr className="cursor-pointer hover:bg-slate-50" onClick={() => onOpen(x.id)} key={x.id}><td className="font-medium">{x.struttura_nome}</td><td>{x.referenti?.[0]?.nome || "—"}</td><td><Badge>{fasi[x.fase] || x.fase}</Badge></td><td>{x.responsabile?.nome || "Non assegnata"}</td><td>{x.prossima_attivita ? `${x.prossima_attivita.testo} · ${String(x.prossima_attivita.scadenza_at).slice(0, 16)}` : "—"}</td></tr>)}</tbody></table></div>;
}

function PartnershipBoard({ items, fasi, onOpen }) {
  return <div className="flex gap-3 overflow-x-auto pb-3">{Object.keys(fasi).map((phase) => { const rows = items.filter((x) => x.fase === phase); return <div className="w-72 shrink-0 rounded-xl border border-admin-border bg-admin-surface p-2" key={phase}><div className="mb-2 flex justify-between font-semibold"><span>{fasi[phase]}</span><Badge variant="secondary">{rows.length}</Badge></div>{rows.length ? rows.map((x) => <button className={`mb-2 w-full rounded-lg border bg-white p-3 text-left ${x.scaduta ? "border-rose-300" : "border-admin-border"}`} key={x.id} onClick={() => onOpen(x.id)}><p className="font-semibold">{x.struttura_nome}</p><p className="text-xs text-admin-muted">{x.referenti?.[0]?.nome || "Referente non indicato"}</p><p className="mt-2 text-xs">{x.responsabile?.nome || "Non assegnata"} · {x.prossima_attivita?.testo || "Senza attività"}</p></button>) : <p className="rounded border border-dashed p-3 text-xs text-admin-muted">Nessuna opportunità.</p>}</div>; })}</div>;
}

function PartnershipDetail({ id, headers, onClose }) {
  const [item, setItem] = useState(null); const [error, setError] = useState(""); const [note, setNote] = useState(""); const [deadline, setDeadline] = useState("");
  const load = useCallback(async () => { try { setItem(await request(`/api/admin/cdrpt/partnership/${id}`, headers)); } catch (e) { setError(e.message); } }, [id]);
  useEffect(() => { load(); }, [load]);
  const saveActivity = async () => { try { await request(`/api/admin/cdrpt/partnership/${id}/attivita`, headers, "POST", { expected_version: item.version, testo: note, scadenza_at: deadline, categoria: "ricontatto" }); setNote(""); setDeadline(""); load(); } catch (e) { setError(e.message); } };
  if (error) return <StateBlock state="error" title={error} onRetry={load} />;
  if (!item) return <StateBlock state="loading" title="Caricamento opportunità…" />;
  return <section className="mx-auto max-w-6xl space-y-4"><Button variant="outline" size="sm" onClick={onClose}>← Elenco partnership</Button><header className="rounded-xl border border-admin-border bg-white p-5"><div className="flex flex-wrap gap-2"><div className="mr-auto"><h1 className="text-2xl font-bold">{item.struttura_nome}</h1><p className="text-sm text-admin-muted">{item.referenti?.[0]?.nome || "Referente non indicato"} · {item.responsabile?.nome || "Responsabile non assegnato"}</p></div><Badge>{item.fase}</Badge></div></header>
    <div className="grid gap-4 lg:grid-cols-2"><section className="rounded-xl border p-4"><h2 className="font-semibold">Relazione e condizioni</h2><p className="mt-2 text-sm">Servizi di interesse: {(item.servizi_interesse || []).join(", ") || "—"}</p><p className="text-sm">Servizi sottoscritti: {(item.servizi_sottoscritti || []).join(", ") || "—"}</p><p className="text-sm">Abbonamento portale e stato rete restano separati nella scheda struttura.</p><h3 className="mt-4 font-medium">Condizioni commissionali</h3>{item.condizioni?.length ? item.condizioni.map((x) => <p className="text-sm" key={x.id}>{x.regime} · decorrenza {x.decorrenza} · {x.stato}</p>) : <p className="text-sm text-admin-muted">Da verificare</p>}</section>
      <section className="rounded-xl border p-4"><h2 className="font-semibold">Prossime attività</h2>{item.attivita?.filter((x) => x.stato === "aperta").map((x) => <p className="mt-2 text-sm" key={x.id}>{x.testo} · {String(x.scadenza_at).slice(0, 16)}</p>)}<div className="mt-4 grid gap-2"><Textarea placeholder="Nuova attività" value={note} onChange={(e) => setNote(e.target.value)} /><Input type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} /><Button size="sm" disabled={!note || !deadline} onClick={saveActivity}>Aggiungi attività</Button></div></section></div>
    <section className="rounded-xl border p-4"><h2 className="font-semibold">Storico</h2>{(item.storico || []).length ? item.storico.map((x) => <p className="mt-2 text-sm" key={x.id}>{x.at?.slice(0, 16)} · {x.utente}: {x.testo}</p>) : <p className="text-sm text-admin-muted">Nessun evento registrato.</p>}</section></section>;
}
